import { useCallback, useEffect, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { getZoneFeatures, setZoneFeature, type FeatureKey, type ZoneFeatures } from '@/api/features';

export const zoneFeaturesKey = (zoneId: string | null | undefined) =>
  zoneId ? ['zone-features', String(zoneId)] : null;

/** The one place the sidebar, the Статистика tabs and Общие настройки ask
 *  «is this function on here». Shared SWR key → one request per zone. */
export function useZoneFeatures(zoneId: string | null | undefined) {
  const swr = useSWR<ZoneFeatures>(
    zoneFeaturesKey(zoneId),
    async () => (await getZoneFeatures(String(zoneId))).data,
    { revalidateOnFocus: false, shouldRetryOnError: false },
  );
  const enable = useCallback(
    async (key: FeatureKey, enabled: boolean, flashPath?: string) => {
      if (!zoneId) return;
      const next = (await setZoneFeature(String(zoneId), key, enabled)).data;
      await swr.mutate(next, { revalidate: false });
      // Feature-specific caches the older pages still read directly.
      void globalMutate((k) => Array.isArray(k) && typeof k[0] === 'string' && /visits-settings|reminders-settings/.test(k[0]));
      void globalMutate(`visits-zone-settings:${zoneId}`);
      if (enabled && flashPath) flashSidebarItem(flashPath);
    },
    [zoneId, swr],
  );
  return { ...swr, features: swr.data, enable };
}

/** Invalidate the registry from pages that flip a flag through their own API. */
export function refreshZoneFeatures(zoneId: string | null | undefined) {
  const key = zoneFeaturesKey(zoneId);
  if (key) void globalMutate(key);
}

// ── «нажал включить — в настройках появилась вкладка и помигала» (M1.6) ──────

const FLASH_EVENT = 'linza:sidebar-flash';

export function flashSidebarItem(path: string) {
  window.dispatchEvent(new CustomEvent(FLASH_EVENT, { detail: path }));
}

/** Path of the sidebar item that should blink right now, or null. */
export function useSidebarFlash(durationMs = 2600) {
  const [path, setPath] = useState<string | null>(null);
  useEffect(() => {
    let timer: number | undefined;
    const onFlash = (e: Event) => {
      setPath((e as CustomEvent<string>).detail);
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setPath(null), durationMs);
    };
    window.addEventListener(FLASH_EVENT, onFlash);
    return () => { window.removeEventListener(FLASH_EVENT, onFlash); window.clearTimeout(timer); };
  }, [durationMs]);
  return path;
}
